import { EncryptedPayload, MergeableReportItem, ReportData } from "~/types/global";
import { decryptReportPayload } from "./encryption.helper";
import { FolderData } from "root/domain/entities/folder.model";
import { BatchReportData, InvalidEntryReport, EncryptedSearchArgs } from "root/domain/entities/batch.model";
import { CryptoResult } from "~/workers/crypto";
import { unwrapDekFromUser } from "~/helpers/envelopeCrypto";
import { base64ToUint8Array } from "~/hooks/useEncryption";
import { CRYPTO_SERVICE } from "~/workers/crypto";

/**
 * Checks if search args are encrypted
 */
const isEncryptedSearchArgs = (searchArgs: any): searchArgs is EncryptedSearchArgs => {
  return searchArgs &&
    typeof searchArgs === 'object' &&
    'iv' in searchArgs &&
    'encrypted' in searchArgs;
};

/**
 * Decrypts invalid entries from a batch item.
 * Uses per-entry envelope decryption (user_wrapped_dek) when available,
 * falling back to the legacy direct-key decryptor for older entries.
 */
const decryptBatchInvalidEntries = async (
  invalidEntries: InvalidEntryReport[],
  decryptData: (data: EncryptedPayload) => Promise<CryptoResult<any>>,
  deriveUserKey?: () => Promise<Uint8Array>,
): Promise<InvalidEntryReport[]> => {
  if (!invalidEntries || invalidEntries.length === 0) {
    return [];
  }

  return Promise.all(
    invalidEntries.map(async (entry) => {
      const searchArgs = entry.report_search_args;

      if (!isEncryptedSearchArgs(searchArgs)) {
        return entry;
      }

      try {
        // Use envelope decryption if the entry has its own DEK
        if (entry.user_wrapped_dek && entry.user_dek_iv && deriveUserKey) {
          const userKey = await deriveUserKey();
          const rawKey = userKey.buffer.slice(userKey.byteOffset, userKey.byteOffset + userKey.byteLength) as ArrayBuffer;
          const dek = await unwrapDekFromUser(rawKey, entry.user_wrapped_dek, entry.user_dek_iv);
          const ciphertext = base64ToUint8Array(searchArgs.encrypted);
          const iv = base64ToUint8Array(searchArgs.iv);
          const { decrypted } = await CRYPTO_SERVICE.decrypt(dek, iv, ciphertext);
          return { ...entry, report_search_args: decrypted };
        }

        // Legacy fallback: direct key decryption
        const { data, success } = await decryptData(searchArgs);
        if (success && data) {
          return { ...entry, report_search_args: data };
        }
      } catch {
        // Expected for entries encrypted with a previous key (pre-envelope or post-password-change).
        // The entry is returned as-is — the encrypted search args will show as "[criptografado]" in the UI.
      }

      return entry;
    })
  );
};

/**
 * Decrypts an item that could be either a report, folder, or batch.
 *
 * @param item - The raw item from the API
 * @param decryptData - Decryptor for the item itself (already envelope-aware for the item's own DEK)
 * @param createDecryptorForItem - Factory that creates an envelope-aware decryptor for a given item.
 *   Required when the item is a folder, so each nested report gets its own DEK-based decryptor.
 */
export const decryptListItem = async (
  item: any,
  decryptData: (data: EncryptedPayload) => Promise<CryptoResult<any>>,
  createDecryptorForItem?: (item: { user_wrapped_dek?: string | null; user_dek_iv?: string | null }) => (data: EncryptedPayload) => Promise<CryptoResult<any>>,
  deriveUserKey?: () => Promise<Uint8Array>,
): Promise<ReportData | FolderData | MergeableReportItem | BatchReportData> => {
  // Handle batch items
  if (item.item_type === 'batch' || item.batch_id) {
    const batchItem = item as BatchReportData;

    if (batchItem.invalid_entries_report_list && batchItem.invalid_entries_report_list.length > 0) {
      const decryptedInvalidEntries = await decryptBatchInvalidEntries(
        batchItem.invalid_entries_report_list,
        decryptData,
        deriveUserKey,
      );

      return {
        ...batchItem,
        invalid_entries_report_list: decryptedInvalidEntries,
      };
    }

    return batchItem;
  }

  const decryptedItem = await decryptReportPayload(item, decryptData);

  if (
    decryptedItem &&
    "folder_id" in decryptedItem &&
    Array.isArray(decryptedItem.data)
  ) {
    decryptedItem.data = await Promise.all(
      decryptedItem.data.map(async (dataItem) => {
        try {
          // Each nested report may have its own user_wrapped_dek — use a per-item decryptor
          const nestedDecryptor = createDecryptorForItem
            ? createDecryptorForItem(dataItem)
            : decryptData;
          return await decryptReportPayload(dataItem, nestedDecryptor);
        } catch {
          // Return item as-is if decryption fails (e.g. encrypted with a different key)
          return dataItem;
        }
      })
    );
  }

  return decryptedItem as ReportData | FolderData;
};
